import { useMemo } from 'react';
import { AgileMacroplanogram } from 'src/components/agile/types/AgileMacroplanogramTypes';
import useGetZoneMapConfig from 'src/store/hooks/useGetZoneMapConfig';

type AllZonesRowData = {
  asin: string;
  uphl2: string;
  remainingFacings: number;
  zoneId: string;
  subcategoryId?: string;
};

export const useZoneCapacityCalculator = (
  storeId: string,
  allZones: AgileMacroplanogram[],
  currentRowData: AllZonesRowData[],
  productGroup?: string,
  zoneMapConfigId?: string,
) => {
  // Prepare input for getZoneMapConfig with proper typing
  const getZoneMapConfigInput = {
    storeId,
    productGroup: productGroup || 'Produce',
    zoneMapConfigName: storeId,
    zoneMapConfigId: zoneMapConfigId || '', // Provide empty string as default for required field
  };

  const { zoneMapConfig } = useGetZoneMapConfig(getZoneMapConfigInput);
  // Calculate static total capacity (independent of currentRowData)
  const allZonesTotalFixtureCapacity = useMemo(() => {
    return allZones.reduce((sum, zone) => {
      const fixtureCount = (zone.fixtureIds || []).length;
      return sum + fixtureCount;
    }, 0);
  }, [allZones]);

  const enhancedData = useMemo(() => {
    if (!currentRowData.length) return [];

    // Create fixture facings map from ZoneMapConfig
    const fixtureToFacingsMap = new Map<string, number>();
    zoneMapConfig?.zones?.forEach((zone) => {
      zone.fixtures?.forEach((fixture) => {
        if (fixture.id && fixture.facings) {
          fixtureToFacingsMap.set(fixture.id, fixture.facings);
        }
      });
    });

    // Create a map of zoneId to zone data
    const zoneDataMap = allZones.reduce(
      (acc, zone) => {
        const fixtureIds = zone.fixtureIds || [];
        const fixtureCount = fixtureIds.length;
        const totalFixtureVolume = fixtureCount;

        const fixtureDetails = fixtureIds.map((fixtureId) => ({
          id: fixtureId,
          facings: fixtureToFacingsMap.get(fixtureId) || 0,
        }));

        const actualTotalFacings = fixtureDetails.reduce((sum, f) => sum + f.facings, 0);
        // Calculate capacity per facing for this zone: total zone volume / total zone facings
        const capacityPerFacing = actualTotalFacings > 0 ? totalFixtureVolume / actualTotalFacings : 0;

        return {
          ...acc,
          [zone.entryId]: {
            zoneName: zone.macroplanogramName || 'Unknown Zone',
            facingCount: zone.maxFacingUnits || 0,
            actualFacingCount: actualTotalFacings,
            fixtureIds,
            fixtureDetails,
            fixtureCount,
            totalFixtureVolume,
            capacityPerFacing,
          },
        };
      },
      {} as Record<
        string,
        {
          zoneName: string;
          facingCount: number;
          actualFacingCount: number;
          fixtureIds: string[];
          fixtureDetails: { id: string; facings: number }[];
          fixtureCount: number;
          totalFixtureVolume: number;
          capacityPerFacing: number;
        }
      >,
    );

    // Enhance current row data with zone info and ASIN capacity
    const result = currentRowData.map((row) => {
      const zoneId = row.zoneId || '';
      const zoneData = zoneDataMap[zoneId];

      // Get pre-calculated capacityPerFacing for this zone
      const capacityPerFacing = zoneData?.capacityPerFacing || 0;

      return {
        ...row,
        zoneName: zoneData?.zoneName || 'Unknown Zone',
        zoneFacingCount: zoneData?.facingCount || 0,
        zoneActualFacingCount: zoneData?.actualFacingCount || 0,
        zoneFixtureDetails: zoneData?.fixtureDetails || [],
        zoneFixtureCount: zoneData?.fixtureCount || 0,
        zoneTotalFixtureCapacity: zoneData?.totalFixtureVolume || 0,
        capacityPerFacing: capacityPerFacing, // Total capacity per facing from all fixtures (mm³)
        subcategoryId: row.subcategoryId, // Pass through subcategoryId
      };
    });

    return result;
  }, [allZones, currentRowData, zoneMapConfig]);

  return { enhancedData, allZonesTotalFixtureCapacity };
};
